import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';
import { ExtendedHttpResponse, HttpInterceptorService } from '@ispm/core/services/http-interceptor.service';
import { IDiagnosticOrder } from '@ispm/test/interfaces/diagnostic-order.interface';
import { BrowserCacheService } from '@ispm/core/services/browser-cache.service';
import { NavigationConfigService } from '@ispm/dashboard/navigation/services/navigationConfig.service';
import _ from 'lodash';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { TestService } from '@ispm/dashboard/test/services/test.service';


@Injectable({
  providedIn: 'root'
})
export class DiagnosticOrderService {

  private baseUrl = '/diagnosticOrder';
  Draft = { }; // This draft is being used in NewOrderCtrl.js hence its being placed asis from old js

  constructor(
    private _http: HttpInterceptorService,
    private browserCacheService: BrowserCacheService,
    private navigationConfigService: NavigationConfigService,
    private authService: AuthenticationService,
    private testService: TestService
  ) {
  }

  /**
   * Update diagnostic order fields
   *
   * @param {any} diagnosticOrderFieldsData
   *
   * @return {Observable<IDiagnosticOrder>}
   */
  updateDiagnosticOrderFields(diagnosticOrderFieldsData: any): Observable<IDiagnosticOrder> {
    return this._http.put(this.baseUrl, diagnosticOrderFieldsData, true)
      .pipe(
        map((res: ExtendedHttpResponse<any>) => res.json())
      );
  }

  /**
   * @method fetchTestIdByContext
   * @description Fetches the current test id based on the organization and app context
   * @param OrganizationId: string => organization ID
   * @param parentTestId: string => parent test id
   */
  fetchTestIdByContext(OrganizationId: string, parentTestId: string): Observable<string> {
    const testIdUrl = `${this.baseUrl}/templateId`;
    if (!parentTestId) {
      return Observable.of(null);
    }
    return this._http.get(testIdUrl, { OrganizationId, parentTestId })
      .map(response => response.json())
      .map(data => data['id'])
      .catch(err => {
        console.log(err);
        return Observable.of(null);
      });
  }
  /*------ New Methods */
  public getTemplates(params) {
    params.cacheId = this.browserCacheService.getCacheId();
    params.application = this.navigationConfigService.getCurrentApplicationId();
    return this._http.get(this.baseUrl + '/templates', { ...params });
  }

  public create(diagnosticOrder, validateSpecimen, disableOrderValidation = false) {
    const url = this.baseUrl + (!!diagnosticOrder.runningMode && diagnosticOrder.runningMode !== 'REGULAR' ? '/' + diagnosticOrder.runningMode : '');

    // Remove unneeded keys from practitioner
    const practitioner: any = { };
    Object.assign(practitioner, this.authService.user);
    delete practitioner.permissions;
    delete practitioner.username;
    delete practitioner.exp;
    delete practitioner.hsdpId;
    delete practitioner.hsdpOrg;
    delete practitioner.xsrf;
    delete practitioner.uiCustomResources;
    delete practitioner.localSettings;
    delete practitioner.loginTime;
    delete this.authService.user.info.meta;

    return this._http.post(url,
      {
        diagnosticOrder: this.formalizeForServer(diagnosticOrder),
        validateSpecimen: validateSpecimen,
        disableOrderValidation: disableOrderValidation,
        application: this.navigationConfigService.getCurrentApplicationId(),
        returnAllOrders: true,
        practitioner: practitioner
      });
  }

  public createOrderNote(order): any {
    const note = {
      actor: {
        reference: this.authService.user.info.id,
        resourceType: 'Practitioner'
      },
      context: [{
        reference: order.workflowRequest.revisions[order.workflowRequest.revisions.length - 1].id,
        resourceType: 'WorkflowRevision'
      }, {
        reference: order.workflowRequest.id,
        resourceType: 'WorkflowRequest'
      }],
      text: order.notes[0],
      parentNote: null
    };

    return this._http.post('/note/', { note: note });
  }

  public formalizeForServer(diagnosticOrder) {
    const specimenMap = new Map();
    diagnosticOrder = Object.assign({ }, diagnosticOrder);
    if (diagnosticOrder.recipients) {
      diagnosticOrder.recipients = diagnosticOrder.recipients.map(recipient => {
        recipient.type = 'Practitioner';
        return recipient;
      });
    }
    if (typeof diagnosticOrder.notes !== 'object' && diagnosticOrder.notes) {
      diagnosticOrder.notes = { 0: diagnosticOrder.notes};
    }
    // A workaround for dynamic form, to be removed when dynamic form can handle dynamic fields
    diagnosticOrder.specimen
    .filter(spec => !_.get(spec, 'accessionIdentifier.value', null))
    .forEach(spec => {
      if (spec.collectedDateTime) {
        spec.collection = { collectionDataTime: spec.collectedDateTime};
      }
      if (spec.sampleId) {
        spec.accessionIdentifier = { value: spec.sampleId};
      }
      if (spec.specimenId) {
        spec.identifiers = [spec.specimenId];
      }
      if (specimenMap.has(spec.objectType)) {
        const currentSpecimen = specimenMap.get(spec.objectType);
        specimenMap.set(spec.objectType, _.merge(spec, currentSpecimen));
      } else {
        specimenMap.set(spec.objectType, spec);
      }
    });
    if (specimenMap.size !== 0) {
      diagnosticOrder.specimen = Array.from(specimenMap.values());
    }

    if (_.has(diagnosticOrder, 'referredByObject')) {
      diagnosticOrder.referredBy = diagnosticOrder.referredByObject.id || diagnosticOrder.referredByObject.code;

      if (_.get(diagnosticOrder, 'referredByObject.identifier[0].value', false)) {
        diagnosticOrder.referringPractitioner = {
          display: diagnosticOrder.referredByObject.text,
          code: diagnosticOrder.referredByObject.identifier[0].value
        };
      }

      delete diagnosticOrder.referredByObject;
    }

    delete diagnosticOrder.ordererObject;
    delete diagnosticOrder.sysDateOfTest;

    if (this.testService.testConfiguration && this.testService.testConfiguration.orderFieldsMapping) {
      this.testService.testConfiguration.orderFieldsMapping.forEach((mapping) => {
        _.set(diagnosticOrder, mapping.target, _.get(diagnosticOrder, mapping.source));
      });
    }

    return diagnosticOrder;
  }


  public formalizeForView(diagnosticOrder, defaultForm, newOrder = true) {
    if (!diagnosticOrder.ordererObject) {
      diagnosticOrder.ordererObject = {
        code: diagnosticOrder.orderer,
        name: [{ text: diagnosticOrder.orderingPractitioner }],
        text: diagnosticOrder.orderingPractitioner
      };
    }

    if (!_.has(diagnosticOrder, 'referredByObject')
      && _.has(diagnosticOrder, 'referredBy')
      && _.has(diagnosticOrder, 'referringPractitioner')) {

        let referredByObject: any;
        referredByObject = { };
      referredByObject['id'] = diagnosticOrder.referredBy;
      referredByObject['code'] = diagnosticOrder.referredBy;
      referredByObject['text'] = diagnosticOrder.referringPractitioner.display;
      referredByObject['name'] = [{ text: referredByObject.text }];
      referredByObject['identifier'] = [{ value: diagnosticOrder.referringPractitioner.code }];
      diagnosticOrder['referredByObject'] = referredByObject;
    }

    if (!diagnosticOrder.recipients) {
      diagnosticOrder.recipients = [];
    } else if (diagnosticOrder.recipients && !(diagnosticOrder instanceof Array)) {
      diagnosticOrder.recipients = Array.from(diagnosticOrder.recipients);
    }

    // for created diagnostic order
    if (diagnosticOrder.workflowRequest && typeof diagnosticOrder.workflowRequest === 'object') {
      diagnosticOrder.sysDateOfTest = diagnosticOrder.workflowRequest.revisions[diagnosticOrder.workflowRequest.revisions.length - 1].datetime;
    }

    // if user is oncologist prevent orderer change
    if (this.authService.user.role.toLowerCase() === 'oncologist' && newOrder) {
      defaultForm.disabledFields = this.testService.testConfiguration.formRoleSettings
        && this.testService.testConfiguration.formRoleSettings.oncologist
        && this.testService.testConfiguration.formRoleSettings.oncologist.disabledFields || [];
      defaultForm.default = {
        ordererObject: {
          id: this.authService.user.info.id,
          text: this.authService.user.info.name[0].text
        },
        orderer: this.authService.user.info.id,
        orderingPractitioner: this.authService.user.info.name[0].text
      };
      Object.assign(diagnosticOrder, defaultForm.default);
    }
    // A workaround for dynamic form, to be removed when dynamic form can handle dynamic fields
    diagnosticOrder.specimen
      .filter(spec => spec.objectType === 'dynamic')
      .forEach(spec => {
        diagnosticOrder.sampleId = spec.accessionIdentifier && spec.accessionIdentifier.value ? spec.accessionIdentifier.value : undefined;
        spec.specimenId = spec.identifiers &&  spec.identifiers.length > 0 ?  spec.identifiers[0] : undefined ;
      });

  }


  public getDiagnosticOrder(id) {
    return this._http.get(this.baseUrl + '/' + id).toPromise();
  }

  public getDiagnosticOrderByTestNumber = function (testNumber) {
    return this._http.get(this.baseUrl + '?identifier=' + testNumber).then(diagnosticOrder => diagnosticOrder.data[0]);
  };

  public createTestAssignNote (workflowId, revisionId, noteContent): any {
    const note = {
      actor: {
        reference: this.authService.user.info.id,
        resourceType: 'Practitioner'
      },
      context: [{
        reference: revisionId,
        resourceType: 'WorkflowRevision'
      }, {
        reference: workflowId,
        resourceType: 'WorkflowRequest'
      }],
      text: noteContent,
      parentNote: null
    };

    return this._http.post('/note/', { note: note });
  }
}
